// Settings (DESIGN.md "Dispositions (console)", ADR 0015; "Console", ADR 0027; "Sign-in", ADR 0032):
// Dispositions for admins and Rep Managers, the company's brief template and sign-in for admins.
// The server refuses reps.

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { isAdmin, requireManager, useMe } from "../../../auth/useMe";
import { Crumbs } from "../../../components/Crumbs";
import { BriefTemplateSettings } from "../../../components/campaigns/BriefTemplateSettings";
import { CampaignSets, CompanyList } from "../../../components/dispositions/DispositionSettings";
import { SignInSettings } from "../../../components/signin/SignInSettings";
import { PageHeader, Segmented, Tabs } from "../../../components/ui";
import { useNavigate } from "../../../lib/navigate";

const search = z.object({
  section: z.enum(["dispositions", "brief-template", "sign-in"]).optional().catch(undefined),
  tab: z.enum(["company", "campaigns"]).optional().catch(undefined),
  campaign: z.string().min(1).max(64).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/calling/dispositions")({
  staticData: { crumb: { label: "Settings", parents: [] } },
  validateSearch: (s) => search.parse(s),
  beforeLoad: requireManager,
  component: SettingsPage,
});

function SettingsPage() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const admin = isAdmin(useMe());
  const section =
    admin && (s.section === "brief-template" || s.section === "sign-in") ? s.section : "dispositions";
  const tab = s.tab ?? (s.campaign ? "campaigns" : "company");
  return (
    <>
      <PageHeader title="Settings" crumbs={<Crumbs />} />
      <Tabs
        label="Settings"
        value={section}
        tabs={[
          { value: "dispositions", label: "Dispositions" },
          ...(admin
            ? [
                { value: "brief-template" as const, label: "Brief template" },
                { value: "sign-in" as const, label: "Sign-in" },
              ]
            : []),
        ]}
        onChange={(v) => void navigate({ search: { section: v }, replace: true })}
      />
      {section === "brief-template" ? (
        <BriefTemplateSettings />
      ) : section === "sign-in" ? (
        <SignInSettings />
      ) : (
        <>
          <div className="mb-4">
            <Segmented
              label="Dispositions"
              value={tab}
              onChange={(v) => void navigate({ search: { tab: v }, replace: true })}
              options={[
                { value: "company", label: "Company list" },
                { value: "campaigns", label: "Campaigns" },
              ]}
            />
          </div>
          {tab === "company" ? (
            <CompanyList />
          ) : (
            <CampaignSets
              selected={s.campaign}
              onSelect={(id) => void navigate({ search: { tab: "campaigns", campaign: id }, replace: true })}
            />
          )}
        </>
      )}
    </>
  );
}
